// entire_file:frontend/src/pages/MeetingWorkspace.js
import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  deleteRefinementStep,
  downloadMeetingArtifact,
  fetchMeetingSession,
  fetchMeetingTemplates,
  publishMeetingSession,
  regenerateMeetingSummary,
  resetMeetingSummary,
  unpublishMeetingSession,
  updateMeetingAttendees,
  renameMeetingSpeakers,
  identifyMeetingSpeakers,
} from '../services/meetings';
import { searchUsers } from '../services/users';
import { useAuth } from '../contexts/AuthContext';
import { 
  TrashIcon, 
  UsersIcon, 
  SparklesIcon, 
  EllipsisVerticalIcon,
  ArrowDownTrayIcon,
  PencilSquareIcon,
  DocumentTextIcon,
  InformationCircleIcon,
  PlayCircleIcon
} from '@heroicons/react/24/outline';
import { Menu, Tab } from '@headlessui/react';
import TranscriptViewer from '../components/TranscriptViewer';
import SummaryViewer from '../components/SummaryViewer';
import ChatPanel from '../components/ChatPanel';
import AudioPlayer from '../components/AudioPlayer';
import { markdownToRtf } from '../utils/markdownRenderers';

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

// --- Speaker Manager Modal ---
const SpeakerManagerModal = ({ isOpen, onClose, sessionId, transcript, onSave, isSaving, onPlaySample }) => {
  const [mapping, setMapping] = useState({});
  const [isDetecting, setIsDetecting] = useState(false);
  const [contextHint, setContextHint] = useState('');

  const speakerStats = useMemo(() => {
    if (!transcript) return [];
    const stats = {};
    transcript.forEach((segment) => {
      const name = segment.speaker || 'Unknown';
      if (!stats[name]) {
        stats[name] = { 
          count: 0, 
          words: 0, 
          longestSegment: null,
          maxDuration: 0 
        };
      }
      
      stats[name].count += 1;
      const wordCount = (segment.text || '').split(/\s+/).length;
      stats[name].words += wordCount;

      // Track longest segment for audio playback
      const start = Number(segment.start || segment.start_time);
      const end = Number(segment.end || segment.end_time);
      if (!Number.isNaN(start) && !Number.isNaN(end)) {
        const duration = end - start;
        if (duration > stats[name].maxDuration) {
          stats[name].maxDuration = duration;
          stats[name].longestSegment = { start, end };
        }
      }
    });
    
    return Object.entries(stats)
      .map(([name, stat]) => ({ name, ...stat }))
      .sort((a, b) => b.words - a.words);
  }, [transcript]);

  useEffect(() => {
    if (isOpen) {
      setMapping({});
      setContextHint('');
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleAutoDetect = async () => {
    setIsDetecting(true);
    try {
      const suggestions = await identifyMeetingSpeakers(sessionId, contextHint);
      setMapping((prev) => {
        const next = { ...prev };
        Object.entries(suggestions).forEach(([key, val]) => {
          if (speakerStats.some((s) => s.name === key)) {
            next[key] = val;
          }
        });
        return next;
      });
      if (Object.keys(suggestions).length === 0) {
        toast('No names could be confidentially identified.', { icon: '🤔' });
      } else {
        toast.success(`Identified ${Object.keys(suggestions).length} speakers!`);
      }
    } catch (error) {
      console.error(error);
      toast.error('Failed to auto-detect speakers');
    } finally {
      setIsDetecting(false);
    }
  };

  const handleSave = () => {
    const changes = {};
    let hasChanges = false;
    speakerStats.forEach((stat) => {
      const original = stat.name;
      const newName = mapping[original];
      if (newName && newName.trim() && newName.trim() !== original) {
        changes[original] = newName.trim();
        hasChanges = true;
      }
    });
    if (!hasChanges) {
      onClose();
      return;
    }
    onSave(changes);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col">
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
          <h3 className="text-lg font-semibold text-gray-900">Manage Speakers</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700 text-2xl leading-none">
            &times;
          </button>
        </div>

        <div className="p-6 overflow-y-auto flex-1">
          <div className="mb-6 bg-indigo-50 border border-indigo-100 rounded-lg p-4">
            <label className="block text-sm font-semibold text-indigo-900 mb-2">
              AI Identification (Optional)
            </label>
            <div className="flex gap-2">
              <textarea
                className="flex-1 text-sm border-indigo-200 rounded-md focus:border-indigo-500 focus:ring-indigo-500 bg-white resize-none"
                rows={2}
                placeholder="Paste attendee list or context here (e.g. 'Dr. Smith, Patient John')..."
                value={contextHint}
                onChange={(e) => setContextHint(e.target.value)}
              />
              <button
                type="button"
                onClick={handleAutoDetect}
                disabled={isDetecting || isSaving}
                className="self-start flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:opacity-50 shadow-sm transition-colors whitespace-nowrap"
              >
                {isDetecting ? (
                  <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <SparklesIcon className="w-4 h-4" />
                )}
                Auto-detect
              </button>
            </div>
          </div>

          <div className="grid grid-cols-[1fr_auto_auto_auto_1fr] gap-4 items-center font-medium text-xs text-gray-500 uppercase tracking-wide mb-2 px-2">
            <div>Original Label</div>
            <div className="text-right">Lines</div>
            <div className="text-right">Words</div>
            <div className="text-center">Audio</div>
            <div>New Name</div>
          </div>

          <div className="space-y-3">
            {speakerStats.map((stat) => (
              <div
                key={stat.name}
                className="grid grid-cols-[1fr_auto_auto_auto_1fr] gap-4 items-center bg-gray-50 p-3 rounded border border-gray-100"
              >
                <div className="font-medium text-gray-700 truncate" title={stat.name}>
                  {stat.name}
                </div>
                <div className="text-right text-gray-500 text-sm w-12">{stat.count}</div>
                <div className="text-right text-gray-500 text-sm w-12">{stat.words}</div>
                <div className="flex justify-center">
                    <button
                        type="button"
                        onClick={() => stat.longestSegment && onPlaySample(stat.longestSegment.start, stat.longestSegment.end)}
                        disabled={!stat.longestSegment}
                        className="text-indigo-600 hover:text-indigo-800 disabled:text-gray-300"
                        title={stat.longestSegment ? `Play ${stat.maxDuration.toFixed(1)}s sample` : 'No audio available'}
                    >
                        <PlayCircleIcon className="w-6 h-6" />
                    </button>
                </div>
                <input
                  type="text"
                  className={`rounded border px-3 py-1.5 text-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 w-full ${
                    mapping[stat.name] ? 'border-indigo-300 bg-indigo-50' : 'border-gray-300'
                  }`}
                  value={mapping[stat.name] || ''}
                  onChange={(e) => setMapping((prev) => ({ ...prev, [stat.name]: e.target.value }))}
                  placeholder="Enter real name..."
                />
              </div>
            ))}
          </div>
        </div>

        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-xl flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-100"
            disabled={isSaving}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="px-4 py-2 bg-indigo-600 rounded-md text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-70"
            disabled={isSaving}
          >
            {isSaving ? 'Updating...' : 'Save & Regenerate'}
          </button>
        </div>
      </div>
    </div>
  );
};

const MeetingWorkspace = () => {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [highlightText, setHighlightText] = useState('');
  const [instructionDraft, setInstructionDraft] = useState('');
  const [regenerating, setRegenerating] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [showResetDialog, setShowResetDialog] = useState(false);
  const [meetingTemplates, setMeetingTemplates] = useState([]);
  const [selectedResetTemplateKey, setSelectedResetTemplateKey] = useState('');
  const [deletingStepId, setDeletingStepId] = useState(null);
  const [unpublishing, setUnpublishing] = useState(false);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [attendeeDrafts, setAttendeeDrafts] = useState([]);
  const [userSearch, setUserSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchingUsers, setSearchingUsers] = useState(false);
  const searchTimerRef = useRef(null);
  const [savingAttendees, setSavingAttendees] = useState(false);
  const [highlightedSegments, setHighlightedSegments] = useState([]);
  const [showSpeakerManager, setShowSpeakerManager] = useState(false);
  const [isRenamingSpeakers, setIsRenamingSpeakers] = useState(false);
  const [accessDenied, setAccessDenied] = useState(false);
  
  // Audio state
  const [audioUrl, setAudioUrl] = useState(null);
  const [seekTime, setSeekTime] = useState(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [seekKey, setSeekKey] = useState(0);
  const [playRange, setPlayRange] = useState(null); // { start, end }

  const { user, loading: authLoading } = useAuth();

  const diarized = useMemo(() => session?.diarized_transcript || [], [session]);
  const refinementSteps = useMemo(() => session?.refinement_steps || [], [session]);
  const isPublished = Boolean(session?.published_at);
  
  // Usage stats
  const usageData = useMemo(() => {
    if (!session?.llm_usage_stats) return null;
    let raw = session.llm_usage_stats;
    if (typeof raw === 'string') {
      try {
        raw = JSON.parse(raw);
      } catch (error) {
        return null;
      }
    }
    const history = Array.isArray(raw) ? raw : (raw.history || [raw]);
    const latest = raw.latest || history[history.length - 1];
    return { latest };
  }, [session]);

  const latestUsage = useMemo(() => usageData?.latest || null, [usageData]);

  useEffect(() => {
    if (Array.isArray(session?.attendees)) {
      setAttendeeDrafts(session.attendees);
    } else {
      setAttendeeDrafts([]);
    }
    setHighlightedSegments([]);
  }, [session]);

  const loadSession = useCallback(async (isBackground = false) => {
    if (!sessionId) return;
    if (!isBackground) setLoading(true);
    try {
      const detail = await fetchMeetingSession(sessionId);
      setSession(detail);
      setAccessDenied(false);
    } catch (error) {
      const status = error?.response?.status;
      if (!isBackground) {
        setSession(null);
        setAccessDenied(status === 403);
        toast.error(status === 403 ? 'You do not have access to this meeting.' : 'Unable to load meeting workspace');
      }
    } finally {
      if (!isBackground) setLoading(false);
    }
  }, [sessionId]);

  useEffect(() => {
    loadSession();
  }, [loadSession]);

  useEffect(() => {
    let pollInterval;
    const isProcessing = session?.status === 'queued' || session?.status === 'processing';
    if (isProcessing) {
      pollInterval = setInterval(() => {
        loadSession(true);
      }, 3000);
    }
    return () => {
      if (pollInterval) clearInterval(pollInterval);
    };
  }, [session?.status, loadSession]);

  useEffect(() => {
    if (session?.audio_download_available) {
      setAudioUrl(`/api/meetings/sessions/${session.id}/download?artifact=audio`);
    } else {
      setAudioUrl(null);
      setSeekTime(null);
      setCurrentTime(0);
      setSeekKey(0);
      setPlayRange(null);
    }
  }, [session]);

  useEffect(() => {
    let active = true;
    const loadTemplates = async () => {
      try {
        const templates = await fetchMeetingTemplates();
        if (active) setMeetingTemplates(Array.isArray(templates) ? templates : []);
      } catch (error) {}
    };
    loadTemplates();
    return () => {
      active = false;
    };
  }, []);

  const handleRegenerate = async () => {
    if (!session) return;
    if (!instructionDraft.trim()) {
      toast.error('Please add an instruction before regenerating');
      return;
    }
    if (isPublished) {
      toast.error('Minutes are published and cannot be regenerated');
      return;
    }
    setRegenerating(true);
    try {
      await regenerateMeetingSummary(session.id, instructionDraft.trim());
      toast.success('Regeneration queued');
      setInstructionDraft('');
      await loadSession();
    } catch (error) {
      toast.error('Unable to queue regeneration');
    } finally {
      setRegenerating(false);
    }
  };

  const openResetDialog = () => {
    if (!session) return;
    if (isPublished) {
      toast.error('Minutes are published and cannot be reset');
      return;
    }
    setSelectedResetTemplateKey('');
    setShowResetDialog(true);
  };

  const closeResetDialog = () => {
    setShowResetDialog(false);
    setSelectedResetTemplateKey('');
  };

  const openShareModal = () => {
    if (!session) return;
    setUserSearch('');
    setSearchResults([]);
    setShareModalOpen(true);
    setAttendeeDrafts(Array.isArray(session.attendees) ? session.attendees : []);
  };

  const closeShareModal = () => {
    setShareModalOpen(false);
    setUserSearch('');
    setSearchResults([]);
    setSearchingUsers(false);
  };

  const handleUserSearch = (value) => {
    setUserSearch(value);
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    const trimmed = value.trim();
    if (!trimmed) {
      setSearchResults([]);
      setSearchingUsers(false);
      return;
    }
    setSearchingUsers(true);
    searchTimerRef.current = setTimeout(async () => {
      try {
        const results = await searchUsers(trimmed);
        setSearchResults(Array.isArray(results) ? results : []);
      } catch (error) {
        toast.error('Unable to search users right now.');
      } finally {
        setSearchingUsers(false);
      }
    }, 500);
  };

  const addAttendeeEmail = (email) => {
    const trimmed = (email || '').trim();
    if (!trimmed) return;
    setAttendeeDrafts((prev) => (prev.includes(trimmed) ? prev : [...prev, trimmed]));
  };

  const removeAttendeeEmail = (email) => {
    setAttendeeDrafts((prev) => prev.filter((item) => item !== email));
  };

  const saveAttendees = async () => {
    if (!session) return;
    setSavingAttendees(true);
    try {
      const updated = await updateMeetingAttendees(session.id, attendeeDrafts);
      setSession(updated);
      toast.success('Attendees updated');
      closeShareModal();
    } catch (error) {
      toast.error('Unable to save attendees');
    } finally {
      setSavingAttendees(false);
    }
  };

  const handleResetConfirm = async () => {
    if (!session) return;
    setResetting(true);
    const overrides = {};
    if (selectedResetTemplateKey) {
      overrides.template_key = selectedResetTemplateKey;
    }
    try {
      await resetMeetingSummary(session.id, overrides);
      toast.success('Summary reset queued');
      setInstructionDraft('');
      setShowResetDialog(false);
      setSelectedResetTemplateKey('');
      await loadSession();
    } catch (error) {
      toast.error('Unable to queue reset');
    } finally {
      setResetting(false);
    }
  };

  const handleDeleteInstruction = async (step) => {
    if (!session || isPublished || !step?.id) return;
    if (!window.confirm('Delete this refinement instruction? This will queue a refresh.')) return;
    setDeletingStepId(step.id);
    try {
      await deleteRefinementStep(session.id, step.id);
      toast.success('Refinement instruction removed');
      await loadSession();
    } catch (error) {
      toast.error('Unable to delete instruction');
    } finally {
      setDeletingStepId(null);
    }
  };

  const handleUnpublish = async () => {
    if (!session) return;
    if (!window.confirm('Unlock these minutes so they can be regenerated again?')) return;
    setUnpublishing(true);
    try {
      await unpublishMeetingSession(session.id);
      toast.success('Minutes unlocked');
      await loadSession();
    } catch (error) {
      toast.error('Unable to unlock minutes');
    } finally {
      setUnpublishing(false);
    }
  };

  const handlePublish = async () => {
    if (!session || isPublished) return;
    setPublishing(true);
    try {
      await publishMeetingSession(session.id);
      toast.success('Minutes published');
      await loadSession();
    } catch (error) {
      toast.error('Unable to publish minutes');
    } finally {
      setPublishing(false);
    }
  };

  const handleBulkRenameSpeakers = async (mapping) => {
    if (!session) return;
    setIsRenamingSpeakers(true);
    try {
      const updated = await renameMeetingSpeakers(session.id, mapping, true);
      setSession(updated);
      toast.success('Speakers updated & summary regeneration queued');
      setShowSpeakerManager(false);
    } catch (error) {
      console.error('Failed to rename speakers', error);
      toast.error('Failed to update speakers');
    } finally {
      setIsRenamingSpeakers(false);
    }
  };

  const handleDownloadRtf = () => {
    if (!session?.llm_output_markdown) return;
    const rawTitle = session.title?.trim() || 'meeting-summary';
    const slug = rawTitle.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-_]/g, '');
    const filename = `${slug || 'meeting-summary'}.rtf`;
    const rtfBlob = new Blob([markdownToRtf(session.llm_output_markdown)], { type: 'application/rtf' });
    const url = URL.createObjectURL(rtfBlob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = filename;
    anchor.click();
    URL.revokeObjectURL(url);
  };

  const formatNumber = (value) => {
    if (value === null || value === undefined || Number.isNaN(Number(value))) return '—';
    return Number(value).toLocaleString();
  };

  const expandHighlightedSegments = (ids) => {
    const normalized = Array.from(new Set((ids || []).map((v) => Number(v)).filter((v) => Number.isFinite(v)))).sort(
      (a, b) => a - b
    );
    const expanded = [];
    normalized.forEach((current, index) => {
      expanded.push(current);
      const next = normalized[index + 1];
      if (typeof next === 'number') {
        const gap = next - current;
        if (gap > 1 && gap <= 10) {
          for (let filler = current + 1; filler < next; filler += 1) expanded.push(filler);
        }
      }
    });
    return expanded;
  };

  const handleChatHighlight = (segmentIds) => {
    if (Array.isArray(segmentIds)) {
      const enriched = expandHighlightedSegments(segmentIds);
      setHighlightedSegments(enriched);
      if (enriched.length) setHighlightText('');
    } else {
      setHighlightedSegments([]);
    }
  };

  const handlePlaySegment = (startTime) => {
    if (typeof startTime === 'number') {
      setSeekTime(startTime);
      setCurrentTime(startTime);
      setSeekKey((prev) => prev + 1);
      setPlayRange(null); // Standard playback, no end range
    }
  };

  const handlePlaySample = (start, end) => {
    if (typeof start === 'number' && typeof end === 'number') {
      setSeekTime(start);
      setCurrentTime(start);
      setPlayRange({ start, end });
      setSeekKey((prev) => prev + 1);
    }
  };

  // Audio time update handler to support range playback (samples)
  const handleTimeUpdate = (time) => {
    setCurrentTime(time);
  };

  const canUnpublish = useMemo(() => {
    if (!session?.published_at || !user) return false;
    const publishedBy = (session.published_by || '').trim();
    const isOwner =
      publishedBy &&
      (publishedBy === user.name || publishedBy === user.email || publishedBy === (user.sub || ''));
    return Boolean(user.is_admin || isOwner);
  }, [session, user]);

  const headerStatus = useMemo(
    () => (session?.status ? session.status.charAt(0).toUpperCase() + session.status.slice(1) : ''),
    [session]
  );
  const usageWarning = useMemo(() => latestUsage?.completion_tokens >= 8000, [latestUsage]);

  if (authLoading) return <div className="p-6">Loading...</div>;

  return (
    <div className="p-4 sm:p-6 bg-gray-50 min-h-screen pb-32">
      {/* Reset Dialog */}
      {showResetDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4 py-8">
          <div className="w-full max-w-lg rounded-xl border border-gray-200 bg-white p-6 shadow-2xl">
            <h3 className="text-xl font-semibold text-gray-900">Reset minutes</h3>
            <p className="mt-2 text-sm text-gray-600">
              Resetting discards refinements and regenerates the summary.
            </p>
            <div className="mt-4 space-y-2">
              <label className="text-sm font-medium text-gray-700">Prompt template</label>
              <select
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                value={selectedResetTemplateKey}
                onChange={(e) => setSelectedResetTemplateKey(e.target.value)}
              >
                <option value="">Use current prompt template</option>
                {meetingTemplates.map((t) => (
                  <option key={t.key} value={t.key}>
                    {t.name} ({t.key})
                  </option>
                ))}
              </select>
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <button className="btn btn-secondary" onClick={closeResetDialog} disabled={resetting}>
                Cancel
              </button>
              <button className="btn btn-danger" onClick={handleResetConfirm} disabled={resetting}>
                {resetting ? 'Resetting...' : 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      )}

      <SpeakerManagerModal
        isOpen={showSpeakerManager}
        onClose={() => setShowSpeakerManager(false)}
        sessionId={session?.id}
        transcript={session?.diarized_transcript}
        onSave={handleBulkRenameSpeakers}
        isSaving={isRenamingSpeakers}
        onPlaySample={handlePlaySample}
      />

      {/* Share Modal */}
      {shareModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4 py-8">
          <div className="w-full max-w-2xl rounded-xl border border-gray-200 bg-white p-6 shadow-2xl">
            <h3 className="text-xl font-semibold text-gray-900">Share meeting</h3>
            <div className="mt-4 space-y-3">
              <div>
                <label className="text-sm font-medium text-gray-700">Search directory</label>
                <input
                  type="text"
                  className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                  placeholder="Type a name or email to search"
                  value={userSearch}
                  onChange={(e) => handleUserSearch(e.target.value)}
                  disabled={savingAttendees}
                />
              </div>
              {userSearch && userSearch.includes('@') && (
                <button
                  className="rounded-md border border-gray-200 bg-gray-50 px-3 py-1 text-sm"
                  onClick={() => addAttendeeEmail(userSearch)}
                  disabled={savingAttendees}
                >
                  Add {userSearch}
                </button>
              )}
              {searchingUsers && <p className="text-sm text-gray-500">Searching users…</p>}
              {!searchingUsers && searchResults.length > 0 && (
                <div className="max-h-48 overflow-y-auto rounded-md border border-gray-200 bg-gray-50">
                  {searchResults.map((result) => (
                    <button
                      key={result.email}
                      className="flex w-full items-start justify-between px-3 py-2 text-left text-sm hover:bg-indigo-50"
                      onClick={() => addAttendeeEmail(result.email)}
                      disabled={savingAttendees}
                    >
                      <div>
                        <p className="font-medium text-gray-900">{result.name}</p>
                        <p className="text-xs text-gray-600">{result.email}</p>
                      </div>
                    </button>
                  ))}
                </div>
              )}
              <div>
                <p className="text-sm font-medium text-gray-700">Current attendees</p>
                <div className="mt-2 flex flex-wrap gap-2">
                  {attendeeDrafts.length ? (
                    attendeeDrafts.map((email) => (
                      <span
                        key={email}
                        className="inline-flex items-center gap-2 rounded-full border border-indigo-100 bg-indigo-50 px-3 py-1 text-xs text-indigo-700"
                      >
                        {email}
                        <button
                          className="text-indigo-500 hover:text-indigo-800"
                          onClick={() => removeAttendeeEmail(email)}
                          disabled={savingAttendees}
                        >
                          ×
                        </button>
                      </span>
                    ))
                  ) : (
                    <p className="text-sm text-gray-500">No attendees have been invited yet.</p>
                  )}
                </div>
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <button
                className="btn btn-secondary"
                onClick={closeShareModal}
                disabled={savingAttendees}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={saveAttendees}
                disabled={savingAttendees}
              >
                {savingAttendees ? 'Saving...' : 'Save attendees'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header Section */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 gap-4">
        <div>
          <button
            className="text-sm text-gray-500 hover:text-gray-900 mb-1 flex items-center gap-1"
            onClick={() => navigate('/meetings')}
          >
            ← Back to Meetings
          </button>
          <h1 className="text-2xl font-bold text-gray-900">
            {session?.title || 'Meeting workspace'}
          </h1>
          {session && (
            <p className="text-sm text-gray-600 flex items-center gap-2 mt-1">
              <span
                className={`inline-block w-2 h-2 rounded-full ${
                  session.status === 'completed'
                    ? 'bg-green-500'
                    : session.status === 'failed'
                      ? 'bg-red-500'
                      : 'bg-yellow-500'
                }`}
              ></span>
              {headerStatus} · Version {session.summary_version || 1}
            </p>
          )}
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          {session && !isPublished && (
            <button
              className="btn bg-indigo-600 text-white hover:bg-indigo-700"
              onClick={handlePublish}
              disabled={publishing}
            >
              {publishing ? 'Publishing...' : 'Publish Minutes'}
            </button>
          )}
          {isPublished && canUnpublish && (
            <button
              className="btn btn-secondary"
              onClick={handleUnpublish}
              disabled={unpublishing}
            >
              {unpublishing ? 'Unlocking...' : 'Unlock'}
            </button>
          )}

          {/* Action Menu for Exports & Tools */}
          <Menu as="div" className="relative inline-block text-left">
            <Menu.Button className="btn btn-secondary flex items-center gap-1">
              Actions
              <EllipsisVerticalIcon className="h-5 w-5" />
            </Menu.Button>
            <Menu.Items className="absolute right-0 z-10 mt-2 w-56 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
              <div className="py-1">
                <Menu.Item>
                  {({ active }) => (
                    <button
                      onClick={openShareModal}
                      className={classNames(
                        active ? 'bg-gray-100 text-gray-900' : 'text-gray-700',
                        'group flex w-full items-center px-4 py-2 text-sm'
                      )}
                    >
                      <UsersIcon className="mr-3 h-5 w-5 text-gray-400" aria-hidden="true" />
                      Share
                    </button>
                  )}
                </Menu.Item>
                <Menu.Item>
                  {({ active }) => (
                    <button
                      onClick={handleDownloadRtf}
                      disabled={!session?.llm_output_markdown}
                      className={classNames(
                        active ? 'bg-gray-100 text-gray-900' : 'text-gray-700',
                        'group flex w-full items-center px-4 py-2 text-sm disabled:opacity-50'
                      )}
                    >
                      <ArrowDownTrayIcon className="mr-3 h-5 w-5 text-gray-400" aria-hidden="true" />
                      Export RTF
                    </button>
                  )}
                </Menu.Item>
                <Menu.Item>
                  {({ active }) => (
                    <button
                      onClick={() => session && downloadMeetingArtifact(session.id, 'summary_md')}
                      disabled={!session?.llm_output_markdown}
                      className={classNames(
                        active ? 'bg-gray-100 text-gray-900' : 'text-gray-700',
                        'group flex w-full items-center px-4 py-2 text-sm disabled:opacity-50'
                      )}
                    >
                      <ArrowDownTrayIcon className="mr-3 h-5 w-5 text-gray-400" aria-hidden="true" />
                      Export Markdown
                    </button>
                  )}
                </Menu.Item>
                {!isPublished && (
                  <>
                    <div className="border-t border-gray-100 my-1"></div>
                    <Menu.Item>
                      {({ active }) => (
                        <button
                          onClick={openResetDialog}
                          className={classNames(
                            active ? 'bg-red-50 text-red-900' : 'text-red-700',
                            'group flex w-full items-center px-4 py-2 text-sm'
                          )}
                        >
                          <TrashIcon className="mr-3 h-5 w-5 text-red-400" aria-hidden="true" />
                          Reset & Regenerate
                        </button>
                      )}
                    </Menu.Item>
                  </>
                )}
              </div>
            </Menu.Items>
          </Menu>
        </div>
      </div>

      {session?.published_at && (
        <div className="mb-6 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800 flex items-center gap-2">
          🔒 Minutes Finalized by <strong>{session.published_by}</strong> on {new Date(session.published_at).toLocaleString()}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-indigo-500"></div>
        </div>
      ) : !session ? (
        <div className="text-center py-20 text-gray-500">
          {accessDenied ? 'You do not have access to this session.' : 'Session not found'}
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 h-[calc(100vh-220px)] min-h-[600px]">
          {/* Left Column: Transcript & Chat */}
          <div className="lg:col-span-2 flex flex-col gap-4 h-full overflow-hidden">
            <div className="bg-white rounded-lg border shadow-sm p-4 flex flex-col h-2/3">
              <div className="flex items-center justify-between mb-3 shrink-0">
                <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                  <DocumentTextIcon className="h-5 w-5" />
                  Transcript
                </h2>
                <div className="flex gap-2">
                  <button
                    onClick={() => setShowSpeakerManager(true)}
                    className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-md border bg-indigo-50 text-indigo-700 hover:bg-indigo-100 transition-colors"
                    disabled={isPublished}
                  >
                    <UsersIcon className="w-3.5 h-3.5" />
                    Speakers
                  </button>
                  <Menu as="div" className="relative inline-block text-left">
                    <Menu.Button className="px-2 py-1.5 text-xs font-medium rounded-md border bg-white hover:bg-gray-50 text-gray-700">
                      Download
                    </Menu.Button>
                    <Menu.Items className="absolute right-0 z-10 mt-1 w-32 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                      <div className="py-1">
                        <Menu.Item>
                          {({ active }) => (
                            <button
                              onClick={() => downloadMeetingArtifact(session.id, 'transcript_text')}
                              className={classNames(active ? 'bg-gray-100' : '', 'block px-4 py-2 text-xs text-gray-700 w-full text-left')}
                            >
                              Text (.txt)
                            </button>
                          )}
                        </Menu.Item>
                        <Menu.Item>
                          {({ active }) => (
                            <button
                              onClick={() => downloadMeetingArtifact(session.id, 'transcript_json')}
                              className={classNames(active ? 'bg-gray-100' : '', 'block px-4 py-2 text-xs text-gray-700 w-full text-left')}
                            >
                              JSON
                            </button>
                          )}
                        </Menu.Item>
                      </div>
                    </Menu.Items>
                  </Menu>
                </div>
              </div>
              <div className="flex-1 overflow-hidden">
                <TranscriptViewer
                  transcript={diarized}
                  highlightText={highlightText}
                  highlightedSegmentIds={highlightedSegments}
                  onPlaySegment={handlePlaySegment}
                  currentTime={currentTime}
                />
              </div>
            </div>

            <div className="flex-1 overflow-hidden">
              <ChatPanel
                sessionId={session.id}
                onHighlight={handleChatHighlight}
                disabled={!diarized.length}
              />
            </div>
          </div>

          {/* Right Column: Tabbed Interface */}
          <div className="bg-white rounded-lg border shadow-sm flex flex-col h-full overflow-hidden">
            <Tab.Group>
              <div className="border-b border-gray-200">
                <Tab.List className="flex -mb-px px-4 gap-4">
                  {['Summary', 'Refine', 'Reasoning', 'Info', 'Debug'].map((tab) => (
                    <Tab
                      key={tab}
                      className={({ selected }) =>
                        classNames(
                          'py-4 text-sm font-medium border-b-2 outline-none transition-colors',
                          selected
                            ? 'border-indigo-500 text-indigo-600'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        )
                      }
                    >
                      {tab}
                    </Tab>
                  ))}
                </Tab.List>
              </div>

              <Tab.Panels className="flex-1 overflow-hidden p-4">
                {/* Summary Tab */}
                <Tab.Panel className="h-full flex flex-col">
                  {session.status === 'failed' && (
                    <div className="bg-red-50 p-4 mb-3 text-red-800 text-sm rounded-lg border border-red-100">
                      Processing failed: {session.last_error || 'unknown error'}. No new minutes were
                      produced; use Reset &amp; Regenerate to try again.
                    </div>
                  )}
                  {session.status === 'completed' && !session.llm_output_markdown ? (
                    <div className="bg-red-50 p-4 text-red-800 text-sm rounded-lg border border-red-100">
                      Generation Blocked by Safety Filters. Try refining your prompt or regeneration.
                    </div>
                  ) : (
                    <div className="flex-1 overflow-y-auto pr-2">
                      <SummaryViewer
                        markdown={session.llm_output_markdown}
                        onTextSelected={setHighlightText}
                      />
                    </div>
                  )}
                </Tab.Panel>

                {/* Refine Tab */}
                <Tab.Panel className="h-full flex flex-col gap-4">
                  <div className="flex-1 overflow-y-auto space-y-3 pr-2">
                    {refinementSteps.length === 0 ? (
                      <p className="text-sm text-gray-500 text-center py-8">
                        No refinement instructions yet. Add one below to tweak the summary.
                      </p>
                    ) : (
                      refinementSteps.map((step) => (
                        <div
                          key={step.id || step.timestamp}
                          className="bg-gray-50 p-3 rounded-lg border border-gray-200 text-sm relative group"
                        >
                          <div className="flex justify-between items-start mb-1">
                            <span className="font-semibold text-gray-900 text-xs">
                              {step.user_name || 'User'}
                            </span>
                            {!isPublished && step.id && (
                              <button
                                onClick={() => handleDeleteInstruction(step)}
                                className="text-gray-400 hover:text-red-600 opacity-0 group-hover:opacity-100 transition-opacity"
                                disabled={deletingStepId === step.id}
                              >
                                {deletingStepId === step.id ? (
                                  <span className="animate-spin h-3 w-3 block border-2 border-red-500 rounded-full border-t-transparent" />
                                ) : (
                                  <TrashIcon className="h-4 w-4" />
                                )}
                              </button>
                            )}
                          </div>
                          <p className="text-gray-700">{step.instruction}</p>
                        </div>
                      ))
                    )}
                  </div>

                  {!isPublished ? (
                    <div className="pt-4 border-t border-gray-100">
                      <label className="block text-xs font-semibold text-gray-700 mb-2">
                        Add Instruction
                      </label>
                      <textarea
                        className="w-full border-gray-300 rounded-md shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm p-3"
                        rows={3}
                        value={instructionDraft}
                        onChange={(e) => setInstructionDraft(e.target.value)}
                        placeholder="E.g. 'Expand on the budget section' or 'Fix typos'..."
                      />
                      <button
                        className="btn btn-primary w-full mt-3 justify-center text-sm"
                        onClick={handleRegenerate}
                        disabled={regenerating}
                      >
                        {regenerating ? (
                          <>
                            <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2" />
                            Updating...
                          </>
                        ) : (
                          <>
                            <PencilSquareIcon className="h-4 w-4 mr-2" />
                            Update Summary
                          </>
                        )}
                      </button>
                    </div>
                  ) : (
                    <div className="bg-amber-50 p-3 rounded border border-amber-200 text-amber-800 text-sm text-center">
                      Minutes are published. Unlock to edit.
                    </div>
                  )}
                </Tab.Panel>

                {/* Reasoning Tab */}
                <Tab.Panel className="h-full flex flex-col">
                  {session.llm_reasoning_text ? (
                    <div className="flex-1 overflow-y-auto bg-gray-50 p-4 rounded-lg border border-gray-200 font-mono text-xs text-gray-700 whitespace-pre-wrap leading-relaxed">
                      {session.llm_reasoning_text}
                    </div>
                  ) : (
                    <div className="flex-1 flex items-center justify-center text-gray-500 text-sm">
                      <p>No reasoning trace available for this session.</p>
                    </div>
                  )}
                </Tab.Panel>

                {/* Info Tab */}
                <Tab.Panel className="h-full overflow-y-auto">
                  <div className="space-y-4">
                    <div>
                      <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                        Token Usage
                      </h4>
                      {usageData ? (
                        <dl className="grid grid-cols-2 gap-4 text-sm">
                          <div className="bg-gray-50 p-3 rounded border border-gray-100">
                            <dt className="text-gray-500 text-xs">Prompt Tokens</dt>
                            <dd className="font-mono font-medium text-gray-900 mt-1">
                              {formatNumber(latestUsage?.prompt_tokens)}
                            </dd>
                          </div>
                          <div className="bg-gray-50 p-3 rounded border border-gray-100">
                            <dt className="text-gray-500 text-xs">Completion Tokens</dt>
                            <dd className="font-mono font-medium text-gray-900 mt-1">
                              {formatNumber(latestUsage?.completion_tokens)}
                            </dd>
                          </div>
                        </dl>
                      ) : (
                        <p className="text-sm text-gray-500">Usage data not available.</p>
                      )}
                      {usageWarning && (
                        <p className="mt-2 text-xs text-amber-600 font-medium flex items-center gap-1">
                          <InformationCircleIcon className="h-4 w-4" />
                          Approaching output token limit.
                        </p>
                      )}
                    </div>

                    <div className="border-t border-gray-100 pt-4">
                      <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                        Session Details
                      </h4>
                      <dl className="space-y-2 text-sm text-gray-700">
                        <div className="flex justify-between">
                          <dt className="text-gray-500">Created</dt>
                          <dd>{new Date(session.created_at).toLocaleString()}</dd>
                        </div>
                        <div className="flex justify-between">
                          <dt className="text-gray-500">Provider</dt>
                          <dd className="capitalize">{session.provider}</dd>
                        </div>
                        <div className="flex justify-between">
                          <dt className="text-gray-500">Model</dt>
                          <dd>{session.llm_model}</dd>
                        </div>
                        <div className="flex justify-between">
                          <dt className="text-gray-500">Audio Size</dt>
                          <dd>{formatNumber(session.audio_file_size_bytes)} bytes</dd>
                        </div>
                      </dl>
                    </div>
                  </div>
                </Tab.Panel>

                {/* Debug Tab */}
                <Tab.Panel className="h-full flex flex-col">
                  <div className="flex-1 overflow-y-auto bg-gray-900 p-4 rounded-lg border border-gray-700 font-mono text-xs text-gray-300 whitespace-pre-wrap leading-relaxed">
                    <div className="mb-4">
                      <strong className="text-indigo-400 block mb-1">LLM Markdown Output (Minutes):</strong>
                      {session.llm_output_markdown || '<empty>'}
                    </div>
                    <div className="mb-4 border-t border-gray-700 pt-4">
                      <strong className="text-indigo-400 block mb-1">LLM Reasoning Text:</strong>
                      {session.llm_reasoning_text || '<empty>'}
                    </div>
                    <div className="border-t border-gray-700 pt-4">
                      <strong className="text-indigo-400 block mb-1">Raw Transcript Sample (first 500 chars):</strong>
                      {session.raw_transcript ? session.raw_transcript.substring(0, 500) + '...' : '<empty>'}
                    </div>
                  </div>
                </Tab.Panel>

              </Tab.Panels>
            </Tab.Group>
          </div>
        </div>
      )}

      {audioUrl && (
        <AudioPlayer
          src={audioUrl}
          startTime={seekTime}
          endTime={playRange?.end}
          autoPlay
          onTimeUpdate={handleTimeUpdate}
          seekKey={seekKey}
        />
      )}
    </div>
  );
};

export default MeetingWorkspace;
